import { Skeleton, TableBody } from '@mui/material';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import _ from 'lodash';
import React from 'react';
import { ApplessOccupancyEventType } from 'utils/applessOccupancyUtils';
import { colours } from 'utils/constants';
import { Order, getComparator } from 'utils/tableUtils';
import { utcToZonedTime, format } from 'date-fns-tz';

export interface ApplessOccupancyLogsData {
  time: Date;
  location: string;
  event: string;
  eventValue?: number;
}

interface ApplessOccupancyLogsHeadCell {
  id: keyof ApplessOccupancyLogsData;
  label: string;
  numeric: boolean;
}

interface EnhancedApplessOccupancyLogsTableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof ApplessOccupancyLogsData
  ) => void;
  order: Order;
  orderBy: string;
}

function getHeadCells(): ApplessOccupancyLogsHeadCell[] {
  const cells: ApplessOccupancyLogsHeadCell[] = [
    {
      id: 'time',
      numeric: false,
      label: 'Date and time'
    },
    {
      id: 'location',
      numeric: false,
      label: 'Location'
    },
    {
      id: 'event',
      numeric: false,
      label: 'Event'
    }
  ];

  return cells;
}

function EnhancedApplessOccupancyLogsTableHead(props: EnhancedApplessOccupancyLogsTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof ApplessOccupancyLogsData) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  const headCells = getHeadCells();

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ fontWeight: 'bold' }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export function ApplessOccupancyLogsTable(props: {
  rows: ApplessOccupancyLogsData[];
  loading: boolean;
  timeZone: string;
}) {
  const { rows, loading, timeZone } = props;

  const [rowOrder, setRowOrder] = React.useState<Order>('desc');
  const [rowOrderBy, setRowOrderBy] = React.useState<keyof ApplessOccupancyLogsData>('time');

  const sortedRows = React.useMemo(
    () => rows.slice().sort(getComparator(rowOrder, rowOrderBy)),
    [rowOrder, rowOrderBy, rows]
  );

  const handleColumnSort = (
    _event: React.MouseEvent<unknown>,
    property: keyof ApplessOccupancyLogsData
  ) => {
    const isAsc = rowOrderBy === property && rowOrder === 'asc';
    setRowOrder(isAsc ? 'desc' : 'asc');
    setRowOrderBy(property);
  };

  if (loading) {
    return <Skeleton variant="rectangular" height={600} />;
  }

  const getEventString = (event: string, eventValue?: number) => {
    if (event === ApplessOccupancyEventType.Set && eventValue !== undefined) {
      return `Set (${eventValue} people)`;
    }
    return _.capitalize(event);
  };

  const getEventColour = (event: string) => {
    switch (event) {
      case ApplessOccupancyEventType.Entry:
        return colours.seafoam;
      case ApplessOccupancyEventType.Exit:
        return 'black';
      case ApplessOccupancyEventType.Set:
      case ApplessOccupancyEventType.Reset:
        return colours.alertRed;
      default:
        return 'black';
    }
  };

  return (
    <>
      <TableContainer>
        <Table size="small" aria-label="Live space utilization">
          <EnhancedApplessOccupancyLogsTableHead
            order={rowOrder}
            orderBy={rowOrderBy}
            onRequestSort={handleColumnSort}
          />
          <TableBody>
            {sortedRows.map((row, index) => {
              const zonedTime = utcToZonedTime(row.time, timeZone); // Convert time to the specified time zone
              const formattedTime = format(zonedTime, 'yyyy-MM-dd hh:mm:ss a', { timeZone }); // Format the time correctly

              return (
                <TableRow hover tabIndex={-1} key={index}>
                  <TableCell component="th" scope="row">
                    {formattedTime}
                  </TableCell>
                  <TableCell>{row.location}</TableCell>
                  <TableCell style={{ fontWeight: 500, color: getEventColour(row.event) }}>
                    {getEventString(row.event, row.eventValue)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
